import { useState } from 'react';
import { useApi } from '../context';
import type { Line, Team } from '../types';
import { AudioField } from './AudioField';
import { DirectoryPicker } from './DirectoryPicker';
import { RoutingPlanEditor } from './RoutingPlanEditor';
import { accountType, typeLabel, type InventoryRow } from '../pages/TeamsPanel';

/**
 * One line's complete setup in a single place: the bot account & number it answers on, the
 * "calls may be recorded" notice (played once, at the start of the call), and the call flow
 * (ordered ring stages with failover and a voicemail terminus). Replaces the old scattered
 * edit-row + separate routing-plan panels.
 */
export function LineSetup({
  line,
  inventory,
  teams,
  onChanged,
}: {
  line: Line;
  inventory: InventoryRow[];
  teams: Team[];
  onChanged: () => void;
}) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [d, setD] = useState({
    name: line.name,
    number: line.number ?? '',
    resourceAccountOid: line.resource_account_oid,
    timeout: line.ring_timeout_seconds?.toString() ?? '',
  });

  const botAccounts = inventory.filter((r) => r.kind === 'resource_account' && accountType(r) === 'bot');
  const numbers = inventory.filter((r) => r.kind === 'phone_number');

  const run = (p: Promise<unknown>) => p.then(onChanged).catch((e: Error) => setError(e.message));

  function saveBasics() {
    run(api.put(`/api/admin/lines/${line.id}`, {
      name: d.name,
      number: d.number || null,
      resourceAccountOid: d.resourceAccountOid,
      ringTimeoutSeconds: d.timeout ? Number(d.timeout) : null,
    }));
  }

  // Persist the recording notice immediately when chosen/cleared.
  function setNotice(blob: string | null) {
    run(api.put(`/api/admin/lines/${line.id}`, { recordingNoticeBlob: blob }));
  }

  return (
    <div className={`line-card${line.is_active ? '' : ' inactive'}`}>
      <div className="filters">
        <strong>{line.name}</strong>
        <span className="sub">{line.number ?? 'no number'}</span>
        <span className={`pill ${line.is_active ? 'ok' : ''}`}>{line.is_active ? 'active' : 'disabled'}</span>
        {line.recording_notice_blob && <span className="pill">recording notice</span>}
        <span className="spacer" />
        <button className="link" onClick={() => setOpen((v) => !v)}>{open ? 'close' : 'set up'}</button>
        <button className="link danger" onClick={() => run(api.put(`/api/admin/lines/${line.id}`, { isActive: !line.is_active }))}>
          {line.is_active ? 'disable' : 'enable'}
        </button>
        <button
          className="link danger"
          onClick={() => {
            if (confirm(`Delete the line "${line.name}"? This removes it from the directory only; it does not change any Teams number assignment.`)) {
              run(api.delete(`/api/admin/lines/${line.id}`));
            }
          }}
        >
          delete
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {open && (
        <div className="line-body">
          <section>
            <h4>1 · Bot &amp; number</h4>
            <div className="filters">
              <label>Name <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} /></label>
              <label>
                Number{' '}
                {numbers.length > 0 ? (
                  <select value={d.number} onChange={(e) => setD({ ...d, number: e.target.value })}>
                    <option value="">— none —</option>
                    {!numbers.some((n) => n.key === d.number) && d.number && <option value={d.number}>{d.number}</option>}
                    {numbers.map((n) => (
                      <option key={n.key} value={n.key}>{n.key} — {String(n.data['assignedName'] ?? '')} [{typeLabel[String(n.data['holderType'])] ?? 'Other'}]</option>
                    ))}
                  </select>
                ) : (
                  <input placeholder="+44…" value={d.number} onChange={(e) => setD({ ...d, number: e.target.value })} style={{ width: '9rem' }} />
                )}
              </label>
              <label>ring each stage <input type="number" min={10} max={600} placeholder="240" style={{ width: '4.5rem' }} value={d.timeout} onChange={(e) => setD({ ...d, timeout: e.target.value })} /> s default</label>
            </div>
            <div className="filters">
              <label>
                Bot resource account{' '}
                <select value={d.resourceAccountOid} onChange={(e) => setD({ ...d, resourceAccountOid: e.target.value })}>
                  {!botAccounts.some((a) => a.key === d.resourceAccountOid) && (
                    <option value={d.resourceAccountOid}>{d.resourceAccountOid}</option>
                  )}
                  {botAccounts.map((a) => <option key={a.key} value={a.key}>{a.name} ({String(a.data['upn'] ?? '')})</option>)}
                </select>
              </label>
              <DirectoryPicker
                label="🔍 find in Teams"
                onPick={(e) => setD((v) => ({ ...v, resourceAccountOid: e.id }))}
              />
              <button className="primary" onClick={saveBasics}>Save</button>
            </div>
            <div className="filters">
              <label>
                Voicemail mailbox{' '}
                <span className="sub">
                  {line.voicemail_user_id ? (line.voicemail_user_name ?? line.voicemail_user_id) : '— deployment default —'}
                </span>
              </label>
              <DirectoryPicker
                label="🔍 choose APP-VM user"
                defaultKind="user"
                onPick={(e) => run(api.put(`/api/admin/lines/${line.id}`, { voicemailUserId: e.id, voicemailUserName: e.displayName }))}
              />
              {line.voicemail_user_id && (
                <button className="link" onClick={() => run(api.put(`/api/admin/lines/${line.id}`, { voicemailUserId: null }))}>use default</button>
              )}
              <span className="sub">The Teams user whose voicemail takes this line's messages (named “APP-VM {line.name}” in Entra).</span>
            </div>
            <div className="filters">
              <label>
                Team{' '}
                <select value={line.team_id ?? ''} onChange={(e) => run(api.put(`/api/admin/lines/${line.id}`, { teamId: e.target.value || null }))}>
                  <option value="">— none (visible to everyone) —</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <span className="sub">Who can see this line's calls in the log.</span>
            </div>
            <p className="hint">
              The bot resource account is the bot's <em>own</em> Teams account the number sits on — never an
              auto attendant or call queue (those would answer the call before the bot sees it). Provision a
              new one under <em>Provision bots &amp; numbers</em> below.
            </p>
          </section>

          <section>
            <h4>2 · Recording notice</h4>
            <p className="hint">Played once, at the very start of the call — the caller never hears it again as the call escalates.</p>
            <AudioField label="“Calls may be recorded” message" blob={line.recording_notice_blob} onChange={setNotice} />
          </section>

          <section>
            <h4>3 · Call flow</h4>
            <p className="hint">
              Ring each stage in order for its timeout; on no answer it escalates to the next (failover). Add a
              message before any stage to play it before that group rings. End with a Voicemail stage to record
              the caller, or leave it off to end the call.
            </p>
            <RoutingPlanEditor lineId={line.id} />
          </section>
        </div>
      )}
    </div>
  );
}
